import { client } from '@api/rest/client';
import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import {
  NOTIFICATION_CENTER_EVENTS,
  type TNotificationCountEventPayload,
} from '@conversed/shared/src/central/constants/socket';
import {
  reconnectAfterRetryHint,
  reconnectAfterServerDisconnect,
} from '@helpers/socketHelpers';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePermissions } from '@hooks/usePermissions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

// * 5-minute fallback poll - corrects any drift from missed socket events.
const FALLBACK_POLL_INTERVAL_MS = 5 * 60 * 1000;

export const useNotificationCounts = () => {
  const { hasPermissions } = usePermissions();
  const {
    login: { accessToken },
  } = useMainSelector();
  const { selectedCompany } = useCompanySelector();
  const companyId = selectedCompany?.id;

  const [trainAiCount, setTrainAiCount] = useState<number | null>(null);
  const [knowledgeArticleCount, setKnowledgeArticleCount] = useState<
    number | null
  >(null);

  const hasTrainAiPermission = hasPermissions([
    PERMISSIONS.TRAINER_AI.TRAINER_AI_VIEW,
  ]);
  const hasKnowledgeArticlePermission = hasPermissions([
    PERMISSIONS.DATA_SOURCE.ARTICLE_ARCHITECT_VIEW,
  ]);

  const shouldConnect =
    (hasTrainAiPermission || hasKnowledgeArticlePermission) && !!companyId;

  const socketRef = useRef<Socket | null>(null);
  // * Holds the pending debounce timer for trainAi refetches so rapid-fire
  // * COUNT events from sequential reviews coalesce into one HTTP request.
  const trainAiRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await client.notifications.getPendingCounts();
      setTrainAiCount(data.trainAiPendingCount);
      setKnowledgeArticleCount(data.knowledgeArticlePendingCount);
    } catch {
      // * Silently swallow fetch errors - a failed fetch should not surface
      // * UI noise for a background indicator.
    }
  }, []);

  const applyCountDelta = useCallback(
    (
      notificationEventType: TNotificationCountEventPayload['type'],
      countUpdater: (current: number) => number,
    ) => {
      const applyToCount = (previous: number | null) => {
        // * null means the user lacks permission for this type - skip the delta.
        if (previous === null) {
          return null;
        }

        return countUpdater(previous);
      };

      if (notificationEventType === 'knowledgeArticle') {
        setKnowledgeArticleCount(applyToCount);
      }
    },
    [],
  );

  // * The trainAi count is distinct hot topics with unreviewed items, not individual
  // * items. A +1/-1 delta would be wrong (e.g. clearing one item from a topic that
  // * still has others leaves the count unchanged). Instead, debounce a refetch so
  // * rapid sequential reviews coalesce into one HTTP call.
  const debouncedFetchTrainAiCounts = useCallback(() => {
    if (trainAiRefetchTimerRef.current !== null) {
      clearTimeout(trainAiRefetchTimerRef.current);
    }

    trainAiRefetchTimerRef.current = setTimeout(() => {
      fetchCounts();
      trainAiRefetchTimerRef.current = null;
    }, 1500);
  }, [fetchCounts]);

  const applyCountAdded = useCallback(
    (payload: TNotificationCountEventPayload) => {
      if (payload.type === 'trainAi') {
        debouncedFetchTrainAiCounts();
      } else if (payload.type === 'knowledgeArticle') {
        applyCountDelta(payload.type, (current) => current + 1);
      }
    },
    [applyCountDelta, debouncedFetchTrainAiCounts],
  );

  const applyCountCleared = useCallback(
    (payload: TNotificationCountEventPayload) => {
      if (payload.type === 'trainAi') {
        debouncedFetchTrainAiCounts();
      } else if (payload.type === 'knowledgeArticle') {
        applyCountDelta(payload.type, (current) => Math.max(0, current - 1));
      }
    },
    [applyCountDelta, debouncedFetchTrainAiCounts],
  );

  useEffect(() => {
    if (!shouldConnect || !accessToken) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      // * companyId lets the backend join this socket to the company:{companyId}
      // * room so notification emits reach all company users without a DB query.
      auth: { token: accessToken, companyId },
    });
    reconnectAfterServerDisconnect(socketRef.current);
    const cancelRetryReconnect = reconnectAfterRetryHint(socketRef.current);

    // * Fetch current counts on every (re)connect - covers initial load and
    // * socket reconnection after a brief drop where we may have missed events.
    socketRef.current.on('connect', fetchCounts);

    socketRef.current.on(
      NOTIFICATION_CENTER_EVENTS.BACKEND_TO_PLATFORM.COUNT_ADDED,
      applyCountAdded,
    );

    socketRef.current.on(
      NOTIFICATION_CENTER_EVENTS.BACKEND_TO_PLATFORM.COUNT_CLEARED,
      applyCountCleared,
    );

    // * 5-minute fallback poll to correct any accumulated drift from missed events.
    const intervalId = setInterval(fetchCounts, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      cancelRetryReconnect();

      clearInterval(intervalId);

      if (trainAiRefetchTimerRef.current !== null) {
        clearTimeout(trainAiRefetchTimerRef.current);
      }

      socketRef.current?.off('connect', fetchCounts);
      socketRef.current?.off(
        NOTIFICATION_CENTER_EVENTS.BACKEND_TO_PLATFORM.COUNT_ADDED,
        applyCountAdded,
      );
      socketRef.current?.off(
        NOTIFICATION_CENTER_EVENTS.BACKEND_TO_PLATFORM.COUNT_CLEARED,
        applyCountCleared,
      );
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [
    shouldConnect,
    accessToken,
    companyId,
    fetchCounts,
    applyCountAdded,
    applyCountCleared,
  ]);

  return { trainAiCount, knowledgeArticleCount };
};
